import { Component, TemplateRef, ViewChild, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { LanguageService } from 'src/app/services/language/language.service';
import { FormsModule } from '@angular/forms';
import { SettingService, FooterSection, FooterItem, FooterItemType, Lang } from 'src/app/services/setting/setting.service';


@Component({
  selector: 'app-setting',
  imports: [CommonModule, FormsModule],
  templateUrl: './setting.component.html',
  styleUrl: './setting.component.css'
})
export class SettingComponent implements OnInit {
  lang: Lang = 'geo';

  loadingFooter = false;

  sections: FooterSection[] = [];
  selectedSectionId: number | null = null;
  selectedSection: FooterSection | null = null;
  items: FooterItem[] = [];

  // quick create section
  newSectionTitle = '';

  // quick create item
  newItem: Partial<FooterItem> = {
    type: 'text',
    label: '',
    value: '',
    url: '',
    icon: '',
    target: 0,
    visible: 1,
  };

  constructor(
    private footer: SettingService,
    private langService: LanguageService
  ) {}

  ngOnInit(): void {
    this.langService.currentLanguage$.subscribe((l: string) => {
      this.lang = (l === 'eng' ? 'eng' : 'geo');
      this.loadSections();
    });
  }

  loadSections() {
    this.loadingFooter = true;
    this.selectedSectionId = null;
    this.selectedSection = null;
    this.items = [];

    this.footer.sections(this.lang).subscribe({
      next: (res) => {
        this.sections = res?.data ?? [];
        this.loadingFooter = false;
      },
      error: () => {
        this.sections = [];
        this.loadingFooter = false;
      },
    });
  }

  selectSection(id: number) {
    this.selectedSectionId = id;
    this.loadingFooter = true;

    this.footer.sectionShow(id, this.lang).subscribe({
      next: (res) => {
        this.selectedSection = res?.data?.section ?? null;
        this.items = res?.data?.items ?? [];
        this.loadingFooter = false;
      },
      error: () => {
        this.selectedSection = null;
        this.items = [];
        this.loadingFooter = false;
      },
    });
  }

  // -------- SECTION actions --------

  addSection() {
    const title = (this.newSectionTitle || '').trim();
    if (!title) return;

    this.footer.sectionCreate({ title, visible: 1 }, this.lang).subscribe({
      next: () => {
        this.newSectionTitle = '';
        this.loadSections();
      },
    });
  }

  saveSectionTitle() {
    if (!this.selectedSection) return;
    this.footer.sectionUpdate(this.selectedSection.id, { title: this.selectedSection.title }, this.lang).subscribe({
      next: () => this.loadSections(),
    });
  }

  toggleSectionVisible(s: FooterSection) {
    this.footer.sectionToggleVisible(s.id, this.lang).subscribe({
      next: (res) => {
        const v = res?.data?.visible ?? (s.visible ? 0 : 1);
        s.visible = v;
      },
    });
  }

  deleteSection(s: FooterSection) {
    if (!confirm('სექცია წაიშალოს?')) return;
    this.footer.sectionDelete(s.id, this.lang, false).subscribe({
      next: () => this.loadSections(),
    });
  }

  moveSection(up: boolean, idx: number) {
    const arr = [...this.sections];
    const to = up ? idx - 1 : idx + 1;
    if (to < 0 || to >= arr.length) return;

    [arr[idx], arr[to]] = [arr[to], arr[idx]];
    this.sections = arr;

    const ids = this.sections.map((x) => x.id);
    this.footer.sectionSort(ids, this.lang).subscribe();
  }

  // -------- ITEM actions --------

  addItem() {
    if (!this.selectedSectionId) return;

    const payload: Partial<FooterItem> = {
      type: (this.newItem.type as FooterItemType) || 'text',
      label: (this.newItem.label ?? '').toString(),
      value: (this.newItem.value ?? '').toString(),
      url: (this.newItem.url ?? '').toString(),
      icon: (this.newItem.icon ?? '').toString(),
      target: Number(this.newItem.target ?? 0),
      visible: Number(this.newItem.visible ?? 1),
    };

    // პატარა ვალიდაცია
    if ((payload.type === 'link' || payload.type === 'social') && !payload.url?.trim()) {
      alert('link/social-სთვის URL აუცილებელია');
      return;
    }
    if (payload.type !== 'link' && payload.type !== 'social' && !payload.value?.trim() && !payload.label?.trim()) {
      alert('მინიმუმ Label ან Value შეავსე');
      return;
    }

    this.footer.itemCreate(this.selectedSectionId, payload, this.lang).subscribe({
      next: () => {
        this.newItem = { type: 'text', label: '', value: '', url: '', icon: '', target: 0, visible: 1 };
        this.selectSection(this.selectedSectionId!);
      },
    });
  }

  saveItem(it: FooterItem) {
    const payload: Partial<FooterItem> = {
      type: it.type,
      label: it.label,
      value: it.value,
      url: it.url,
      icon: it.icon,
      target: it.target,
      visible: it.visible,
    };

    this.footer.itemUpdate(it.id, payload, this.lang).subscribe();
  }

  toggleItemVisible(it: FooterItem) {
    this.footer.itemToggleVisible(it.id, this.lang).subscribe({
      next: (res) => {
        it.visible = res?.data?.visible ?? (it.visible ? 0 : 1);
      },
    });
  }

  deleteItem(it: FooterItem) {
    if (!confirm('ელემენტი წაიშალოს?')) return;
    this.footer.itemDelete(it.id, this.lang, false).subscribe({
      next: () => {
        this.items = this.items.filter((x) => x.id !== it.id);
      },
    });
  }

  moveItem(up: boolean, idx: number) {
    if (!this.selectedSectionId) return;

    const arr = [...this.items];
    const to = up ? idx - 1 : idx + 1;
    if (to < 0 || to >= arr.length) return;

    [arr[idx], arr[to]] = [arr[to], arr[idx]];
    this.items = arr;

    const ids = this.items.map((x) => x.id);
    this.footer.itemSort(this.selectedSectionId, ids, this.lang).subscribe();
  }

  targetLabel(t: number) {
    return Number(t) === 1 ? 'ახალ ფანჯარაში' : 'იმავე ფანჯარაში';
  }
}